/**
 * SalesApprovalHistoryPage — Phase 33.10.
 *
 * Read-only audit trail of all approval decisions from the last 90 days.
 */

import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiBaseUrl } from '@/api/http';
import { getAccessToken } from '@/api/supabase';
import type { ApprovalItem } from './SalesApprovalsPage';
import { statusColor, fmtQueued, itemTypeLabel } from './SalesApprovalsPage';

// ── Data ─────────────────────────────────────────────────────────────────────

interface HistoryResponse {
  items: ApprovalItem[];
  total: number;
}

async function fetchHistory(): Promise<HistoryResponse> {
  const token = await getAccessToken();
  const resp = await fetch(`${apiBaseUrl()}/sales/approvals/history?days=90`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json() as Promise<HistoryResponse>;
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function SalesApprovalHistoryPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { data, isPending, isError } = useQuery({
    queryKey: ['sales', 'approvals', 'history'],
    queryFn: fetchHistory,
    refetchInterval: 30_000,
  });

  const items = data?.items ?? [];

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA',       to: '/omega' },
          { label: 'Sales',       to: '/omega/sales' },
          { label: 'Approval queue', to: '/omega/sales/approvals' },
          { label: 'History' },
        ]}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '11pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', direction: 'ltr' }}>
          History — last 90 days
        </h1>
        {data && (
          <span style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3, fontSize: '8pt', padding: '1px 7px', color: 'var(--omega-ink-subtle)' }}>
            {items.length}
          </span>
        )}
        <Link
          to="/omega/sales/approvals"
          style={{
            marginLeft: 'auto',
            background: 'rgba(125,211,252,0.12)',
            border: '1px solid rgba(125,211,252,0.35)',
            borderRadius: 4,
            color: '#7dd3fc',
            fontFamily: 'var(--omega-mono)',
            fontSize: '8.5pt',
            fontWeight: 600,
            padding: '5px 12px',
            textDecoration: 'none',
          }}
        >
          ← Back to queue
        </Link>
      </div>

      {/* Column headers */}
      {!isPending && !isError && items.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: '60px 1fr 100px 130px 130px 90px 1fr',
          gap: 10,
          padding: '6px 12px',
          borderBottom: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
          marginBottom: 2,
        }}>
          {['Type', 'Subject', 'Lead', 'Arrived', 'Decided', 'Status', 'Rejection reason'].map(h => (
            <span key={h} style={{ fontSize: '8.5pt', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--omega-ink-subtle)', direction: 'ltr' }}>
              {h}
            </span>
          ))}
        </div>
      )}

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError   && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading history.</div>}

      {!isPending && !isError && items.length === 0 && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic', padding: '40px 0', textAlign: 'center', direction: 'ltr' }}>
          No approval history in the last 90 days.
        </div>
      )}

      {items.map(item => (
        <div
          key={item.item_id}
          style={{
            display: 'grid',
            gridTemplateColumns: '60px 1fr 100px 130px 130px 90px 1fr',
            gap: 10,
            alignItems: 'center',
            padding: '10px 12px',
            borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.06))',
          }}
        >
          <span style={{
            fontSize: '7.5pt',
            padding: '1px 6px',
            border: '1px solid rgba(255,255,255,0.15)',
            color: 'var(--omega-ink-subtle)',
            borderRadius: 3,
            textAlign: 'center',
            direction: 'ltr',
          }}>
            {itemTypeLabel(item.item_type)}
          </span>

          <div style={{ overflow: 'hidden' }}>
            <div style={{ fontSize: '9.5pt', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', direction: 'ltr' }}>
              {item.subject}
            </div>
            {item.preview_snippet && (
              <div style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', direction: 'ltr' }}>
                {item.preview_snippet}
              </div>
            )}
          </div>

          <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.lead_id}
          </span>

          <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>
            {fmtQueued(item.queued_at)}
          </span>

          <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>
            {item.decided_at ? fmtQueued(item.decided_at) : '—'}
          </span>

          <span style={{
            fontSize: '7.5pt',
            padding: '1px 6px',
            border: `1px solid ${statusColor(item.status)}`,
            color: statusColor(item.status),
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontWeight: 700,
            borderRadius: 3,
            justifySelf: 'start',
          }}>
            {item.status}
          </span>

          <span style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', direction: 'ltr' }}>
            {item.rejection_reason ?? '—'}
          </span>
        </div>
      ))}
    </div>
  );
}
